import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Users, Gift, TrendingUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export function SiteAdminReferralsTab() {
  const [referrals, setReferrals] = useState<any[]>([]);
  const [stats, setStats] = useState({ total: 0, activated: 0, rewarded: 0, totalMonths: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReferrals();
  }, []);

  const fetchReferrals = async () => {
    setLoading(true);
    try {
      // Fetch all referrals
      const { data } = await supabase
        .from('referrals')
        .select('*')
        .order('created_at', { ascending: false });

      const allReferrals = data || [];
      setReferrals(allReferrals);

      // Fetch all referral codes to get PTA names
      const { data: codes } = await supabase
        .from('pta_referral_codes')
        .select('pta_id, code');

      // Calculate stats
      const activated = allReferrals.filter(r => r.status === 'activated').length;
      const rewarded = allReferrals.filter(r => r.status === 'rewarded').length;

      // Get total months credited
      const { data: rewards } = await supabase
        .from('referral_rewards')
        .select('months_credited');

      const totalMonths = (rewards || []).reduce((sum, r) => sum + r.months_credited, 0);

      setStats({
        total: allReferrals.length,
        activated,
        rewarded,
        totalMonths,
      });
    } catch (error) {
      console.error('Error fetching referrals:', error);
    } finally {
      setLoading(false);
    }
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'pending': return <Badge variant="secondary">Pending</Badge>;
      case 'activated': return <Badge className="bg-blue-100 text-blue-800">Activated</Badge>;
      case 'rewarded': return <Badge className="bg-green-100 text-green-800">Rewarded</Badge>;
      case 'expired': return <Badge variant="outline">Expired</Badge>;
      default: return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-muted-foreground">Total Referrals</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{stats.activated}</p>
                <p className="text-sm text-muted-foreground">Activated</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Gift className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{stats.rewarded}</p>
                <p className="text-sm text-muted-foreground">Rewarded</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Gift className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{stats.totalMonths}</p>
                <p className="text-sm text-muted-foreground">Months Banked</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Referrals Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Referrals</CardTitle>
          <CardDescription>Platform-wide referral overview</CardDescription>
        </CardHeader>
        <CardContent>
          {referrals.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No referrals yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Referrer PTA</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reward</TableHead>
                  <TableHead>Activated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referrals.map((referral) => (
                  <TableRow key={referral.id}>
                    <TableCell>{new Date(referral.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="font-mono text-xs">{referral.referrer_pta_id.substring(0, 8)}...</TableCell>
                    <TableCell>{statusBadge(referral.status)}</TableCell>
                    <TableCell>
                      {referral.reward_months > 0
                        ? `${referral.reward_months} month${referral.reward_months > 1 ? 's' : ''} banked`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {referral.activated_at
                        ? new Date(referral.activated_at).toLocaleDateString()
                        : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
